// In-app survey delivery ("embed API"). A survey configured with the
// `in_app` channel is not drawn by Weloop: the widget hands it to the host
// page and the customer's own code renders it, then answers through the
// handle it received. This module is the host-page half of that exchange –
// pure, DOM-free, so the protocol can be unit-tested; `bootstrap.ts` wires it
// to postMessage.
//
//   widget → page  weloop:survey-embed         { survey, appearance }
//   page → widget  weloop:survey-embed-shown    { surveyId }   page code used it
//   page → widget  weloop:survey-embed-answer   { requestId, surveyId, score, comment?, isPartial? }
//                                               or { requestId, surveyId, answers }
//   page → widget  weloop:survey-embed-dismiss  { surveyId }
//   widget → page  weloop:survey-embed-result   { requestId, surveyId, ok, error? }
//   page → widget  weloop:survey-embed-draft    { surveyId, answers }   no reply
//
// Page code sees none of this: it subscribes with
// `weloop("on", "survey", handler)` and receives a `SurveyHandle`. The
// handle's `kind` tells the page what it holds: a score survey (NPS, CSAT)
// answered with one score, or a questionnaire answered with one value per
// question.
//
// Most pages call `survey.mount(element)` and let Weloop draw its own card
// there (`survey-card-mount.ts`), in the project's widget styling. Pages that
// want their own design read the fields and answer through the handle.
//
// Embedded surveys do not share a slot: the widget hands over every eligible
// one, and a page can show several at once. Handlers usually keep only the
// survey they render (`if (survey.id !== "…") return;`), so receiving a
// survey is not showing it. A survey counts as shown – one impression row –
// only once page code uses it: reads its copy, mounts it, answers or
// dismisses it. Reading `id` and `kind` to pick one is free. An impression
// starts no cooldown: only a dismissal counts toward the reminder rules.

import type { SurveyCardAppearance } from "@weloop/core/widget/survey-card-frame";

import { parseSurveyCardAppearance } from "@weloop/core/widget/survey-card-frame";

import type {
	MountedSurvey,
	SurveyCardAnswer,
	SurveyCardDraft,
	SurveyMountOptions,
} from "./survey-card-mount";

export type { MountedSurvey, SurveyMountOptions } from "./survey-card-mount";

/** A score survey as page code sees it. Public API – add fields, never rename. */
export interface EmbeddedScoreSurvey {
	/** Optional follow-up question to show once a score is picked; null when the survey has none. */
	readonly commentQuestion: string | null;
	readonly id: string;
	readonly kind: "csat" | "nps";
	readonly question: string;
	readonly scale: {
		readonly highLabel: string | null;
		readonly kind: "numeric" | "smileys" | "stars" | "thumbs";
		readonly lowLabel: string | null;
		readonly max: number;
		readonly min: number;
	};
	readonly title: string;
}

export type EmbeddedQuestionType =
	| "date"
	| "image_choice"
	| "long_text"
	| "multi_choice"
	| "rating"
	| "scale"
	| "short_text"
	| "single_choice";

/** One image a visitor can pick in an `image_choice` question. */
export interface EmbeddedQuestionImage {
	readonly caption?: string;
	readonly id: string;
	/** Short-lived signed URL – render it now, never store it. */
	readonly url: string;
}

/** One question of a questionnaire as page code sees it. */
export interface EmbeddedQuestion {
	readonly id: string;
	readonly imageOptions?: readonly EmbeddedQuestionImage[];
	readonly label: string;
	/**
	 * Display text per option in the visitor's language, index-aligned with
	 * `options`. Absent when the copy is served in the source language.
	 * Answers always carry the `options` value, never the label.
	 */
	readonly optionLabels?: readonly string[];
	readonly options?: readonly string[];
	readonly placeholder?: string;
	readonly required: boolean;
	/** Bounds and edge captions of a `rating` or `scale` question. */
	readonly scale?: {
		readonly high?: string;
		readonly low?: string;
		readonly max: number;
		readonly min: number;
		readonly symbol?: string;
	};
	readonly type: EmbeddedQuestionType;
}

/** One questionnaire answer: text, a number, or the picked option values. */
export type QuestionnaireAnswerValue = number | string | readonly string[];

/**
 * A questionnaire as page code sees it: an intro and a question list. `draft`
 * holds what the visitor answered so far in this occurrence – Weloop saves a
 * questionnaire as it is filled in, so a form drawn by the page can open
 * prefilled. Absent when nothing was saved yet.
 */
export interface EmbeddedQuestionnaire {
	readonly draft?: Readonly<Record<string, QuestionnaireAnswerValue>>;
	readonly id: string;
	readonly intro: string;
	readonly kind: "custom";
	readonly questions: readonly EmbeddedQuestion[];
	readonly title: string;
}

export type EmbeddedSurvey = EmbeddedQuestionnaire | EmbeddedScoreSurvey;

/** What page code answers a score survey with. `comment` is optional and trimmed. */
export interface SurveyAnswer {
	readonly comment?: string;
	/**
	 * True on a score sent before the comment step: Weloop saves it, and a
	 * survey that asks visitors to sign in to validate waits for the final
	 * call – without the flag – before it opens the panel on its sign-in
	 * screen. Leave it out of the last call, comment or not.
	 */
	readonly isPartial?: boolean;
	readonly score: number;
}

/** What page code answers a questionnaire with: one value per question id. */
export interface QuestionnaireAnswer {
	readonly answers: Readonly<Record<string, QuestionnaireAnswerValue>>;
}

/**
 * The object handed to `survey` event handlers: the survey plus the two
 * actions the page can take. `answer` resolves once Weloop stored the
 * response and rejects with a short reason otherwise; it may be called again
 * to update the answer (a later comment, a changed score, a corrected
 * answer set) within the survey's cooldown. `dismiss` tells Weloop the user
 * closed the survey without answering, so it is not asked again before the
 * cooldown ends.
 */
export interface SurveyHandleActions {
	/**
	 * A score for an NPS or CSAT handle, an answer set for a questionnaire
	 * handle. The other shape rejects before anything is posted.
	 */
	readonly answer: (input: QuestionnaireAnswer | SurveyAnswer) => Promise<void>;
	readonly dismiss: () => void;
	/**
	 * Draws Weloop's survey card inside `target` (an element or a CSS
	 * selector) in the project's widget styling, and stores the visitor's
	 * answer. Calling it again moves the card to the new target.
	 */
	readonly mount: (
		// oxlint-disable-next-line typescript-eslint/prefer-readonly-parameter-types -- Element is an external mutable DOM interface
		target: Element | string,
		options?: SurveyMountOptions,
	) => MountedSurvey;
}

export type ScoreSurveyHandle = EmbeddedScoreSurvey & SurveyHandleActions;

export type QuestionnaireHandle = EmbeddedQuestionnaire & SurveyHandleActions;

export type SurveyHandle = QuestionnaireHandle | ScoreSurveyHandle;

const isObject = (raw: unknown): raw is Readonly<Record<string, unknown>> =>
	typeof raw === "object" && raw !== null;

const nullableString = (raw: unknown): string | null | undefined => {
	if (raw === null || typeof raw === "string") {
		return raw;
	}
	return undefined;
};

const isStringArray = (raw: unknown): raw is readonly string[] =>
	Array.isArray(raw) && raw.every((item) => typeof item === "string");

const isAnswerValue = (value: unknown): value is QuestionnaireAnswerValue =>
	typeof value === "string" ||
	typeof value === "number" ||
	isStringArray(value);

const QUESTION_TYPES: readonly EmbeddedQuestionType[] = [
	"date",
	"image_choice",
	"long_text",
	"multi_choice",
	"rating",
	"scale",
	"short_text",
	"single_choice",
];

const isQuestionType = (raw: unknown): raw is EmbeddedQuestionType =>
	typeof raw === "string" &&
	(QUESTION_TYPES as readonly string[]).includes(raw);

const optionalString = (raw: unknown): string | undefined =>
	typeof raw === "string" ? raw : undefined;

function parseScale(raw: unknown): EmbeddedQuestion["scale"] | undefined {
	if (
		!isObject(raw) ||
		typeof raw["min"] !== "number" ||
		typeof raw["max"] !== "number"
	) {
		return undefined;
	}
	return {
		high: optionalString(raw["high"]),
		low: optionalString(raw["low"]),
		max: raw["max"],
		min: raw["min"],
		symbol: optionalString(raw["symbol"]),
	};
}

/** Every image needs an id and a URL – one malformed entry drops the question. */
function parseImages(
	raw: readonly unknown[],
): readonly EmbeddedQuestionImage[] | undefined {
	const parsed: EmbeddedQuestionImage[] = [];
	for (const image of raw) {
		if (
			!isObject(image) ||
			typeof image["id"] !== "string" ||
			typeof image["url"] !== "string"
		) {
			return undefined;
		}
		parsed.push({
			caption: optionalString(image["caption"]),
			id: image["id"],
			url: image["url"],
		});
	}
	return parsed;
}

/** Narrows one question; the optional fields are copied only when well-formed. */
function parseQuestion(raw: unknown): EmbeddedQuestion | undefined {
	if (!isObject(raw)) {
		return undefined;
	}
	const { id, label, required, type } = raw;
	if (
		typeof id !== "string" ||
		typeof label !== "string" ||
		typeof required !== "boolean" ||
		!isQuestionType(type)
	) {
		return undefined;
	}
	const question: {
		-readonly [K in keyof EmbeddedQuestion]: EmbeddedQuestion[K];
	} = { id, label, required, type };
	if (isStringArray(raw["options"])) {
		question.options = raw["options"];
	}
	if (isStringArray(raw["optionLabels"])) {
		question.optionLabels = raw["optionLabels"];
	}
	if (typeof raw["placeholder"] === "string") {
		question.placeholder = raw["placeholder"];
	}
	const scale = parseScale(raw["scale"]);
	if (scale !== undefined) {
		question.scale = scale;
	}
	const images = raw["imageOptions"];
	if (Array.isArray(images)) {
		const parsed = parseImages(images);
		if (parsed === undefined) {
			return undefined;
		}
		question.imageOptions = parsed;
	}
	return question;
}

function parseQuestionnaire(
	raw: Readonly<Record<string, unknown>>,
): EmbeddedQuestionnaire | undefined {
	const { draft, id, intro, questions, title } = raw;
	if (
		typeof id !== "string" ||
		typeof intro !== "string" ||
		typeof title !== "string" ||
		!Array.isArray(questions) ||
		questions.length === 0
	) {
		return undefined;
	}
	const parsed: EmbeddedQuestion[] = [];
	for (const question of questions) {
		const item = parseQuestion(question);
		if (item === undefined) {
			return undefined;
		}
		parsed.push(item);
	}
	const questionnaire: EmbeddedQuestionnaire = {
		id,
		intro,
		kind: "custom",
		questions: parsed,
		title,
	};
	// A malformed draft only costs the prefill, never the survey.
	if (!isObject(draft)) {
		return questionnaire;
	}
	const answers: Record<string, QuestionnaireAnswerValue> = {};
	for (const [questionId, value] of Object.entries(draft)) {
		if (!isAnswerValue(value)) {
			return questionnaire;
		}
		answers[questionId] = value;
	}
	return { ...questionnaire, draft: answers };
}

/**
 * Narrows the widget's `weloop:survey-embed` payload. Anything malformed is
 * dropped: a stale bundle must never hand page code a half-built object.
 */
export function parseEmbeddedSurvey(raw: unknown): EmbeddedSurvey | undefined {
	if (!isObject(raw)) {
		return undefined;
	}
	if (raw["kind"] === "custom") {
		return parseQuestionnaire(raw);
	}
	const { commentQuestion, id, kind, question, scale, title } = raw;
	if (
		typeof id !== "string" ||
		typeof question !== "string" ||
		typeof title !== "string" ||
		(kind !== "csat" && kind !== "nps") ||
		!isObject(scale)
	) {
		return undefined;
	}
	const comment = nullableString(commentQuestion);
	const lowLabel = nullableString(scale["lowLabel"]);
	const highLabel = nullableString(scale["highLabel"]);
	const { kind: scaleKind, max, min } = scale;
	if (
		comment === undefined ||
		lowLabel === undefined ||
		highLabel === undefined ||
		(scaleKind !== "numeric" &&
			scaleKind !== "smileys" &&
			scaleKind !== "stars" &&
			scaleKind !== "thumbs") ||
		typeof min !== "number" ||
		typeof max !== "number"
	) {
		return undefined;
	}
	return {
		commentQuestion: comment,
		id,
		kind,
		question,
		scale: { highLabel, kind: scaleKind, lowLabel, max, min },
		title,
	};
}

// What page code reads to pick the survey it renders. Reading them is not
// using the survey.
const ROUTING_FIELDS: ReadonlySet<string> = new Set(["id", "kind"]);

/**
 * Turns every copy field of the handle (title, question, scale, questions and
 * so on) into a getter that reports the first read: a page reading the copy
 * is rendering the survey. Actions report themselves when called.
 */
function trackContentReads(
	// oxlint-disable-next-line typescript-eslint/prefer-readonly-parameter-types -- the handle is redefined in place, before page code sees it
	handle: SurveyHandle,
	onRead: () => void,
): void {
	for (const key of Object.keys(handle)) {
		const value: unknown = Reflect.get(handle, key);
		if (ROUTING_FIELDS.has(key) || typeof value === "function") {
			continue;
		}
		Object.defineProperty(handle, key, {
			enumerable: true,
			get: () => {
				onRead();
				return value;
			},
		});
	}
}

/** Timer handle type, so tests can inject fake timers without casts. */
export type TimerHandle = ReturnType<typeof setTimeout>;

/** What the embed hands the card mounter – everything but the DOM context. */
export interface SurveyCardMountRequest {
	readonly answer: SurveyCardAnswer;
	readonly appearance: SurveyCardAppearance | undefined;
	readonly dismiss: () => void;
	/** The card's answers so far, saved without a verdict. */
	readonly draft: SurveyCardDraft;
	readonly options: SurveyMountOptions | undefined;
	readonly survey: EmbeddedSurvey;
	// oxlint-disable-next-line typescript-eslint/prefer-readonly-parameter-types -- Element is an external mutable DOM interface
	readonly target: Element | string;
}

export interface SurveyEmbedDependencies {
	readonly clearTimeout?: (handle: TimerHandle) => void;
	/**
	 * Deliver the handle to `survey` listeners; returns how many received it.
	 * Zero means nobody is listening yet – the survey is kept and delivered
	 * when the first listener subscribes.
	 */
	readonly deliver: (handle: SurveyHandle) => number;
	/** Draw the card in the page – `mountSurveyCard` with the widget's origin and look. */
	readonly mount: (
		// oxlint-disable-next-line typescript-eslint/prefer-readonly-parameter-types -- the request carries the target Element, an external mutable DOM interface
		request: SurveyCardMountRequest,
	) => MountedSurvey;
	/** Post a message to the widget iframe. */
	readonly post: (message: Readonly<Record<string, unknown>>) => void;
	/** Timer hooks, injectable for tests. */
	readonly setTimeout?: (fn: () => void, ms: number) => TimerHandle;
}

/** How long `answer()` waits for the widget's confirmation. */
export const ANSWER_TIMEOUT_MS = 15_000;

export interface SurveyEmbed {
	/** A `weloop:survey-embed-result` message arrived from the widget. */
	readonly onResult: (message: Readonly<Record<string, unknown>>) => void;
	/** A `survey` listener was added – deliver a survey that arrived earlier. */
	readonly onSubscribed: () => void;
	/** A `weloop:survey-embed` message arrived from the widget. */
	readonly onSurvey: (raw: unknown, rawAppearance?: unknown) => void;
	/** The widget iframe was replaced (project switch): forget everything. */
	readonly reset: () => void;
}

export function createSurveyEmbed(deps: SurveyEmbedDependencies): SurveyEmbed {
	const schedule = deps.setTimeout ?? ((fn, ms) => setTimeout(fn, ms));
	const cancel =
		deps.clearTimeout ??
		((handle) => {
			clearTimeout(handle);
		});

	// Surveys that arrived before any listener, in arrival order. The widget
	// sends each embedded survey once per page lifetime.
	let waiting: SurveyHandle[] = [];
	// Answers awaiting the widget's confirmation, by request id.
	const pending = new Map<
		string,
		{
			readonly reject: (reason: Readonly<Error>) => void;
			readonly resolve: (status: string | undefined) => void;
			readonly timer: TimerHandle;
		}
	>();
	let nextRequest = 0;
	// Cards drawn by `mount()`, removed on reset: their iframe talks to a
	// widget that is gone, so an answer there could never be stored.
	const cards = new Set<MountedSurvey>();
	// Set by reset. Handles the page kept from before then answer to nobody,
	// so they reject at once instead of waiting out the timeout.
	let isReset = false;

	const settle = (
		requestId: string,
		error: string | undefined,
		status?: string,
	): void => {
		const entry = pending.get(requestId);
		if (entry === undefined) {
			return;
		}
		pending.delete(requestId);
		cancel(entry.timer);
		if (error === undefined) {
			entry.resolve(status);
		} else {
			entry.reject(new Error(`[weloop] survey answer failed: ${error}`));
		}
	};

	// Posts one answer message and waits for the widget's verdict on it: the
	// status it carries, when the answer was parked rather than stored.
	const send = async (
		surveyId: string,
		payload: Readonly<Record<string, unknown>>,
	): Promise<string | undefined> => {
		nextRequest += 1;
		const requestId = `${surveyId}:${String(nextRequest)}`;
		// oxlint-disable-next-line promise/avoid-new -- bridges a postMessage round-trip to a promise; no helper exists for that
		return await new Promise<string | undefined>((resolve, reject) => {
			const timer = schedule(() => {
				settle(requestId, "timeout");
			}, ANSWER_TIMEOUT_MS);
			pending.set(requestId, { reject, resolve, timer });
			if (isReset) {
				settle(requestId, "reset");
				return;
			}
			deps.post({
				...payload,
				requestId,
				surveyId,
				type: "weloop:survey-embed-answer",
			});
		});
	};

	const dismiss = (surveyId: string) => (): void => {
		deps.post({ surveyId, type: "weloop:survey-embed-dismiss" });
	};

	// The mounted card's autosave. The widget validates the set; the page
	// never learns the outcome, Submit stores everything regardless.
	const draftFor =
		(survey: EmbeddedSurvey): SurveyCardDraft =>
		(answers) => {
			if (survey.kind !== "custom" || !isObject(answers)) {
				return;
			}
			deps.post({
				answers,
				surveyId: survey.id,
				type: "weloop:survey-embed-draft",
			});
		};

	// Page code and the mounted card both answer through here, so one set of
	// runtime checks guards both. `unknown` because neither caller is trusted.
	// `beforeSend` runs once the input passed the checks, right before it is
	// posted: a rejected input is not a use of the survey.
	const answerFor =
		(survey: EmbeddedSurvey, beforeSend: () => void) =>
		async (input: unknown): Promise<string | undefined> => {
			if (survey.kind === "custom") {
				const answers = isObject(input) ? input["answers"] : undefined;
				if (
					!isObject(answers) ||
					!Object.values(answers).every((value) => isAnswerValue(value))
				) {
					throw new TypeError(
						"[weloop] questionnaire answer needs { answers: { [questionId]: string | number | string[] } }",
					);
				}
				beforeSend();
				return await send(survey.id, { answers });
			}
			const score = isObject(input) ? input["score"] : undefined;
			if (typeof score !== "number" || !Number.isInteger(score)) {
				throw new TypeError("[weloop] survey answer needs an integer score");
			}
			const rawComment = isObject(input) ? input["comment"] : undefined;
			const comment =
				typeof rawComment === "string" && rawComment.trim() !== ""
					? rawComment.trim()
					: undefined;
			// A score sent before the comment step (the mounted card's, or page
			// code's `isPartial`): the widget saves it but holds the sign-in
			// step of a sign-in survey for the final answer.
			const isPartial = isObject(input) && input["isPartial"] === true;
			beforeSend();
			return await send(survey.id, {
				comment,
				score,
				...(isPartial ? { isPartial } : {}),
			});
		};

	const makeHandle = (
		survey: EmbeddedSurvey,
		appearance: SurveyCardAppearance | undefined,
	): SurveyHandle => {
		// The first use reports the survey as shown; later ones are no-ops.
		let isUsed = false;
		const markUsed = (): void => {
			if (isUsed) {
				return;
			}
			isUsed = true;
			deps.post({ surveyId: survey.id, type: "weloop:survey-embed-shown" });
		};
		const answer = answerFor(survey, markUsed);
		const dismissSurvey = dismiss(survey.id);
		const dismissUsed = (): void => {
			markUsed();
			dismissSurvey();
		};
		// One card per survey: mounting again moves it.
		let mounted: MountedSurvey | undefined;
		const mount: SurveyHandleActions["mount"] = (target, options) => {
			markUsed();
			mounted?.unmount();
			const card = deps.mount({
				answer,
				appearance,
				dismiss: dismissSurvey,
				draft: draftFor(survey),
				options,
				survey,
				target,
			});
			mounted = card;
			cards.add(card);
			return card;
		};
		const handle: SurveyHandle = {
			...survey,
			// Page code gets a plain promise: whether the answer was stored or
			// parked until a sign-in is Weloop's business, the panel says so.
			answer: async (input) => {
				await answer(input);
			},
			dismiss: dismissUsed,
			mount,
		};
		trackContentReads(handle, markUsed);
		return handle;
	};

	// True when at least one `survey` listener received the handle.
	const deliver = (handle: SurveyHandle): boolean => deps.deliver(handle) > 0;

	return {
		onResult: (message) => {
			const { error, ok, requestId, status } = message;
			if (typeof requestId !== "string") {
				return;
			}
			if (ok === true) {
				settle(
					requestId,
					undefined,
					typeof status === "string" ? status : undefined,
				);
				return;
			}
			settle(requestId, typeof error === "string" ? error : "unknown");
		},
		onSubscribed: () => {
			waiting = waiting.filter((handle) => !deliver(handle));
		},
		onSurvey: (raw, rawAppearance) => {
			const survey = parseEmbeddedSurvey(raw);
			if (survey === undefined) {
				return;
			}
			// A widget older than `mount()` sends no appearance; the card then
			// wears Weloop's default look.
			const handle = makeHandle(
				survey,
				parseSurveyCardAppearance(rawAppearance),
			);
			if (!deliver(handle)) {
				waiting.push(handle);
			}
		},
		reset: () => {
			isReset = true;
			waiting = [];
			for (const card of cards) {
				card.unmount();
			}
			for (const [requestId] of pending) {
				settle(requestId, "reset");
			}
		},
	};
}
